import React from 'react';
import { Shield, Mail, CheckCircle2, Info, FileText } from 'lucide-react';

export default function DataDeletionPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Facebook Data Deletion</h1>
              <p className="text-gray-600 mt-1">Instructions for RAHS Marketing Automation App</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <p className="text-gray-700 leading-relaxed">
              This page explains how we handle data deletion requests for users of our Facebook-integrated 
              marketing automation application. We are committed to protecting your privacy and complying 
              with Facebook's Platform Policies.
            </p>
          </div>
        </div>

        {/* What Data We Collect */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Info className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-semibold text-gray-900">What Data We Collect</h2>
          </div>
          
          <p className="text-gray-700 mb-4">
            When you use our Facebook app, we may collect and store the following information:
          </p>

          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span><strong>Facebook Page ID:</strong> To identify which Facebook Page you manage</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span><strong>Page Access Tokens:</strong> To post content on your behalf (encrypted and secure)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span><strong>Post Content:</strong> Content you create through our platform for Facebook posting</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span><strong>Engagement Metrics:</strong> Post performance data (likes, comments, shares)</span>
            </li>
          </ul>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> We do not collect or store personal information from Facebook users who view 
              or interact with your posts. We only store data related to your business's Facebook Page management.
            </p>
          </div>
        </div>

        {/* How to Request Deletion */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-semibold text-gray-900">How to Request Data Deletion</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Email Request</h3>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-900">Send an email to:</span>
                </div>
                <a 
                  href="mailto:privacy@rightawaygroup.com?subject=Facebook%20Data%20Deletion%20Request"
                  className="text-lg font-semibold text-blue-600 hover:text-blue-700 underline"
                >
                  privacy@rightawaygroup.com
                </a>
                
                <div className="mt-4 p-4 bg-white rounded border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Include in your email:</p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• Your Facebook User ID (find at facebook.com/me)</li>
                    <li>• Your Facebook Page name (if applicable)</li>
                    <li>• Brief description of what data you want deleted</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            <h2 className="text-2xl font-semibold text-gray-900">What Happens After You Request</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Confirmation (Within 24 hours)</h3>
                <p className="text-gray-600">
                  We'll send you an email confirming we received your deletion request.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Processing (Within 30 days)</h3>
                <p className="text-gray-600 mb-2">
                  We'll permanently delete all your Facebook-related data from our systems, including:
                </p>
                <ul className="ml-4 space-y-1 text-sm text-gray-600">
                  <li>• Page access tokens</li>
                  <li>• Scheduled posts</li>
                  <li>• Post analytics data</li>
                  <li>• Any Facebook-specific settings</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-semibold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Completion Notification</h3>
                <p className="text-gray-600">
                  Once deletion is complete, we'll send you a final confirmation email.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
            <Info className="w-5 h-5" />
            Important Notes
          </h3>
          <ul className="space-y-2 text-sm text-amber-800">
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Data deletion is <strong>permanent and cannot be undone</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Deleting data will disconnect our app from your Facebook Page</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Content already posted to Facebook will remain on Facebook</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Other account data (non-Facebook related) will not be affected</span>
            </li>
          </ul>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Questions or Concerns?</h2>
          <p className="text-gray-700 mb-6">
            If you have questions about our data practices or the deletion process, please contact us:
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-600">Privacy Team</p>
                <a 
                  href="mailto:privacy@rightawaygroup.com" 
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  privacy@rightawaygroup.com
                </a>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-600">Company</p>
                <p className="font-medium text-gray-900">Right Away Home Services</p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This data deletion policy is compliant with Facebook Platform Policy 5.a 
              (Protecting User Information and Respecting User Choice).
            </p>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <a
            href="https://marketing.rightawayhome.com"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Marketing Platform
          </a>
        </div>
      </div>
    </div>
  );
}
